'use client'

import type { Language } from 'codegen/types'
import * as React from 'react'
import { useCookies } from 'react-cookie'
import invariant from 'tiny-invariant'
import { getCookieDomain } from 'utils/env'
import { LANGUAGE_COOKIE_NAME } from 'constants/cookies'

import { validateLocale, validateStudioLocale } from './helpers'

const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN || process.env.VITE_COOKIE_DOMAIN
invariant(cookieDomain, 'Cookie domain is not defined inside cf-i18n/cookies')

const path = '/'

export const makeUseLangCookie = <TLocale extends string = Language>(
  isValidLocale: (value: unknown) => boolean = validateLocale,
) => {
  return () => {
    const [cookies, setCookie, removeCookie] = useCookies([LANGUAGE_COOKIE_NAME])

    const rawCookie = cookies[LANGUAGE_COOKIE_NAME]
    const langCookie = isValidLocale(rawCookie) ? (rawCookie as TLocale) : null

    const setLangCookie = React.useCallback(
      (lang: TLocale) => {
        // Fix to prevent infinite redirects due to cookie set on an unexpected domain from wordpress
        removeCookie(LANGUAGE_COOKIE_NAME, { path })
        removeCookie(LANGUAGE_COOKIE_NAME, { path, domain: 'staging.creativefabrica.com' })
        // End-fix

        const domain = getCookieDomain(cookieDomain)
        setCookie(LANGUAGE_COOKIE_NAME, lang, { domain, path })
      },
      [setCookie, removeCookie],
    )

    const removeLangCookie = React.useCallback(() => {
      const domain = getCookieDomain(cookieDomain)
      removeCookie(LANGUAGE_COOKIE_NAME, { domain, path })
    }, [removeCookie])

    return [langCookie, setLangCookie, removeLangCookie] as const
  }
}

export const useLangCookie = makeUseLangCookie<Language>()

/**
 * Studio and its sister apps (freemode, lumo) support the Language Push batch
 * that marketplace / My Account don't yet. Reading the cookie with the default
 * `useLangCookie` (marketplace-only `validateLocale`) rejects those locales and
 * silently falls back to `defaultLanguage`, even though the cookie itself was
 * set correctly. Use this hook wherever Studio reads/writes the language cookie.
 */
export const useStudioLangCookie = makeUseLangCookie<Language>(validateStudioLocale)

/**
 * Reactive reader for the raw `lang` cookie value — no locale allowlist.
 * Use in contexts that need the user's picked locale for targeting or
 * analytics but should not couple to any product's list of renderable
 * locales. Returns `null` when the cookie is missing or empty.
 */
const isNonEmptyString = (value: unknown): boolean => typeof value === 'string' && value.length > 0

export const useRawLangCookie = makeUseLangCookie<string>(isNonEmptyString)
